import { api } from '../../services/api';
import { sichereKarte, type Karte } from '../../components/karten/kartenSchema';
import type { StummDauer } from '../../components/karten/reaktionLogik';

/** Backend-Vertrag: core/backend/src/dashboard/dashboardRoutes.ts */
export interface Briefing {
  text: string;
  erstelltAm: string;
}

export interface PersoenlichDaten {
  kapitaen: { name: string };
  briefing: Briefing | null;
  vorschlaege: Karte[];
  dauerauftraege: unknown[];
  /** Nutzung der letzten 30 Tage je Programm — vom Backend geliefert, im Dashboard nicht mehr angezeigt (03.10.2026). */
  meineProgramme: Array<{ programmeId: string; anzahl30Tage: number }>;
  /** Persönliche Einstellungen (Farbstufe); null = noch nie gewählt. */
  einstellungen: { farbstufe: string | null };
}

export type ReaktionBody =
  | { bewertung: 'hoch' }
  | { bewertung: 'runter'; grund: string; alsRegel?: boolean }
  | { bewertung: 'stumm'; stummBis: StummDauer };

export const DASHBOARD_NICHT_FREIGESCHALTET = 'DASHBOARD_NICHT_FREIGESCHALTET';

/** True, wenn der Fehler das 403-Gate des Dashboards ist. */
export function istNichtFreigeschaltet(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: { error?: { code?: string } } } };
  return e?.response?.status === 403 && e.response.data?.error?.code === DASHBOARD_NICHT_FREIGESCHALTET;
}

/** True bei 409 VORSCHLAG_BEREITS_ENTSCHIEDEN. */
export function istBereitsEntschieden(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: { error?: { code?: string } } } };
  return e?.response?.status === 409 && e.response.data?.error?.code === 'VORSCHLAG_BEREITS_ENTSCHIEDEN';
}

/** Fehlertext fuer die Anzeige (Backend-Meldung, sonst Fallback). */
export function fehlerText(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: { message?: string } } } };
  return e?.response?.data?.error?.message || fallback;
}

function kartenListe(raw: unknown): Karte[] {
  return Array.isArray(raw) ? raw.map(sichereKarte) : [];
}

export async function ladePersoenlich(): Promise<PersoenlichDaten> {
  const { data } = await api.get('/dashboard/persoenlich');
  const d = data?.data ?? {};
  return {
    kapitaen: { name: typeof d.kapitaen?.name === 'string' ? d.kapitaen.name : 'Kalle' },
    briefing:
      typeof d.briefing?.text === 'string' && d.briefing.text.trim() !== '' && typeof d.briefing.erstelltAm === 'string'
        ? { text: d.briefing.text, erstelltAm: d.briefing.erstelltAm }
        : null,
    vorschlaege: kartenListe(d.vorschlaege),
    dauerauftraege: Array.isArray(d.dauerauftraege) ? d.dauerauftraege : [],
    meineProgramme: Array.isArray(d.meineProgramme) ? d.meineProgramme : [],
    einstellungen: { farbstufe: typeof d.einstellungen?.farbstufe === 'string' ? d.einstellungen.farbstufe : null },
  };
}

export interface ReaktionErgebnis {
  regelGemerkt: boolean;
  regelHinweis?: string;
}

/** Backend antwortet 200 `{status, regelGemerkt, regelHinweis}`; 409 VORSCHLAG_BEREITS_ENTSCHIEDEN wirft. */
export async function sendeReaktion(vorschlagId: string, body: ReaktionBody): Promise<ReaktionErgebnis> {
  const { data } = await api.post(`/dashboard/vorschlaege/${encodeURIComponent(vorschlagId)}/reaktion`, body);
  const d = data?.data ?? {};
  return {
    regelGemerkt: d.regelGemerkt === true,
    regelHinweis: typeof d.regelHinweis === 'string' && d.regelHinweis ? d.regelHinweis : undefined,
  };
}

export async function sendeErledigt(vorschlagId: string): Promise<void> {
  await api.post(`/dashboard/vorschlaege/${encodeURIComponent(vorschlagId)}/erledigt`);
}

export async function ladeVorschlaege(): Promise<Karte[]> {
  const { data } = await api.get('/dashboard/vorschlaege', { params: { status: 'offen' } });
  return kartenListe(data?.data);
}

export interface VerlaufEintrag {
  runId: number | string;
  frage: string;
  antwort: string;
  erstelltAm: string;
  kostenUsd: number;
  karten: Karte[];
}

export async function ladeVerlauf(limit = 20): Promise<VerlaufEintrag[]> {
  const { data } = await api.get('/dashboard/verlauf', { params: { limit } });
  const liste = Array.isArray(data?.data) ? data.data : [];
  return liste.map((e: Record<string, unknown>) => ({
    runId: (e.runId as number | string) ?? '',
    frage: typeof e.frage === 'string' ? e.frage : '',
    antwort: typeof e.antwort === 'string' ? e.antwort : '',
    erstelltAm: typeof e.erstelltAm === 'string' ? e.erstelltAm : '',
    kostenUsd: Number(e.kostenUsd) || 0,
    karten: kartenListe(e.karten),
  }));
}

export interface GedaechtnisEintrag {
  id: number | string;
  art: string;
  inhalt: string;
  quelle: string;
  berichtigt: boolean;
  createdAt: string;
}

export async function ladeGedaechtnis(): Promise<GedaechtnisEintrag[]> {
  const { data } = await api.get('/dashboard/gedaechtnis');
  const liste = Array.isArray(data?.data) ? data.data : [];
  return liste.map((e: Record<string, unknown>) => ({
    id: (e.id as number | string) ?? '',
    art: typeof e.art === 'string' ? e.art : '',
    inhalt: typeof e.inhalt === 'string' ? e.inhalt : '',
    quelle: typeof e.quelle === 'string' ? e.quelle : '',
    berichtigt: e.berichtigt === true,
    createdAt: typeof e.createdAt === 'string' ? e.createdAt : '',
  }));
}

export async function loescheGedaechtnis(id: number | string): Promise<void> {
  await api.delete(`/dashboard/gedaechtnis/${encodeURIComponent(String(id))}`);
}

/** Backend antwortet 202 mit der Ausfuehrungs-Id. */
export async function starteHeartbeat(): Promise<{ executionId: string }> {
  const { data } = await api.post('/dashboard/heartbeat');
  return { executionId: String(data?.data?.executionId ?? '') };
}

export interface FrageAntwort {
  antwort: string;
  runId: number | string;
  kostenUsd: number;
  dauerMs: number;
  delegationen: Array<{ spielerId: string; frage: string }>;
  karten: Karte[];
}

/** Frage an den Kapitaen. Lauf kann bis ~2 Min dauern (Delegation an den Analysten). */
export async function sendeFrage(frage: string): Promise<FrageAntwort> {
  const { data } = await api.post('/dashboard/frage', { frage }, { timeout: 130000 });
  const d = data?.data ?? {};
  return {
    antwort: typeof d.antwort === 'string' ? d.antwort : '',
    runId: d.runId ?? '',
    kostenUsd: Number(d.kostenUsd) || 0,
    dauerMs: Number(d.dauerMs) || 0,
    delegationen: Array.isArray(d.delegationen) ? d.delegationen : [],
    karten: kartenListe(d.karten),
  };
}

export interface WarumDaten {
  werkzeug: string;
  parameter: unknown;
  runId: string;
  zeit: string;
}

/** Warum-Sprung einer Karte: Werkzeugaufruf, aus dem die Karte entstand (Server maskiert Parameter). */
export async function ladeWarum(runId: string | number, werkzeug: string): Promise<WarumDaten> {
  const { data } = await api.get('/dashboard/warum', { params: { runId, werkzeug } });
  const d = data?.data ?? {};
  return {
    werkzeug: typeof d.werkzeug === 'string' ? d.werkzeug : werkzeug,
    parameter: d.parameter ?? null,
    runId: String(d.runId ?? runId),
    zeit: typeof d.zeit === 'string' ? d.zeit : '',
  };
}

/** 409 BEISPIEL_NICHT_AENDERBAR: Beispielkarten lassen sich nicht ändern. */
export function istBeispielKonflikt(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: { error?: { code?: string } } } };
  return e?.response?.status === 409 && e.response.data?.error?.code === 'BEISPIEL_NICHT_AENDERBAR';
}

export interface Ansicht {
  id: string;
  name: string;
  beispiel: boolean;
}

export async function ladeAnsichten(): Promise<Ansicht[]> {
  const { data } = await api.get('/dashboard/ansichten');
  const liste = Array.isArray(data?.data?.ansichten) ? data.data.ansichten : [];
  return liste
    .filter((a: Record<string, unknown>) => typeof a.id === 'string')
    .map((a: Record<string, unknown>) => ({
      id: a.id as string,
      name: typeof a.name === 'string' ? a.name : (a.id as string),
      beispiel: a.beispiel === true,
    }));
}

export interface KartenZeile {
  id: string;
  ansicht: string;
  zone: string;
  abschnitt: string | null;
  breite: number;
  reihenfolge: number;
  wichtig: boolean;
  stummBis: string | null;
  /** Eigener Titel (Umbenennen); null = Titel der Karte. */
  titelEigen: string | null;
  beispiel: boolean;
  karte: unknown;
}

export interface KartenAntwort {
  karten: KartenZeile[];
  datenstand: string | null;
  kostenHeuteUsd: number | null;
}

export async function ladeKarten(ansicht: string): Promise<KartenAntwort> {
  const { data } = await api.get('/dashboard/karten', { params: { ansicht } });
  const d = data?.data ?? {};
  const liste: Array<Record<string, unknown>> = Array.isArray(d.karten) ? d.karten : [];
  return {
    karten: liste.map((z) => ({
      id: String(z.id ?? ''),
      ansicht: typeof z.ansicht === 'string' ? z.ansicht : ansicht,
      zone: typeof z.zone === 'string' ? z.zone : 'haupt',
      abschnitt: typeof z.abschnitt === 'string' ? z.abschnitt : null,
      breite: Number(z.breite) || 6,
      reihenfolge: Number(z.reihenfolge) || 0,
      wichtig: z.wichtig === true,
      stummBis: typeof z.stummBis === 'string' ? z.stummBis : null,
      titelEigen: typeof z.titelEigen === 'string' && z.titelEigen.trim() ? z.titelEigen : null,
      beispiel: z.beispiel === true,
      karte: z.karte,
    })),
    datenstand: typeof d.datenstand === 'string' ? d.datenstand : null,
    kostenHeuteUsd: Number.isFinite(Number(d.kostenHeuteUsd)) && d.kostenHeuteUsd != null ? Number(d.kostenHeuteUsd) : null,
  };
}

export interface AnheftenEingabe {
  runId: number | string;
  index: number;
  zone: string;
  ansicht: string;
  abschnitt?: string;
  breite?: number;
}

/** Sendet nur runId + Index (nie Karteninhalt) — der Server liest die Karte aus dem Lauf. */
export async function heftAntwortKarteAn(eingabe: AnheftenEingabe): Promise<{ id: string }> {
  const { data } = await api.post('/dashboard/karten', eingabe);
  return { id: String(data?.data?.id ?? '') };
}

export interface VerschiebeZiel {
  zone: string;
  abschnitt?: string | null;
  /** Vor diese Karte setzen (Vorrang vor nachId). */
  vorId?: string;
  /** Hinter diese Karte setzen; ohne beides ans Ende der Zone. */
  nachId?: string;
}

export interface KartenPatch {
  zone?: string;
  reihenfolge?: 'hoch' | 'runter';
  wichtig?: boolean;
  stummBis?: string | null;
  /** Spaltenbreite 1–12. */
  breite?: number;
  /** Umbenennen; null = eigenen Titel löschen. */
  titel?: string | null;
  /** Drag & Drop. */
  verschiebe?: VerschiebeZiel;
}

export async function aendereKarte(id: string, patch: KartenPatch): Promise<void> {
  await api.put(`/dashboard/karten/${encodeURIComponent(id)}`, patch);
}

export async function loescheKarte(id: string): Promise<void> {
  await api.delete(`/dashboard/karten/${encodeURIComponent(id)}`);
}

export type AuffaelligkeitStatus = 'erledigt' | 'ignoriert' | 'offen';

/** Wächter-Eintrag (Tore-Spezialist) bearbeiten; der Server heftet die Wächter-Karten danach neu. */
export async function bearbeiteAuffaelligkeit(ref: string, status: AuffaelligkeitStatus, notiz?: string): Promise<void> {
  await api.put('/dashboard/tore/auffaelligkeiten', { ref, status, ...(notiz && notiz.trim() ? { notiz: notiz.trim() } : {}) });
}

/** Widget-Raster: Position/Größe/eigener Titel je Widget (Schlüssel siehe widgetLayout.kartenSchluessel). */
export interface LayoutEintrag { i: string; x: number; y: number; w: number; h: number; titel?: string }

function leseLayoutEintraege(roh: unknown): LayoutEintrag[] {
  if (!Array.isArray(roh)) return [];
  return roh.flatMap((e) => {
    const o = e as Record<string, unknown>;
    const zahl = (v: unknown) => (typeof v === 'number' && Number.isInteger(v) ? v : null);
    const [x, y, w, h] = [zahl(o?.x), zahl(o?.y), zahl(o?.w), zahl(o?.h)];
    if (typeof o?.i !== 'string' || x === null || y === null || w === null || h === null) return [];
    return [{ i: o.i, x, y, w, h, ...(typeof o.titel === 'string' && o.titel.trim() ? { titel: o.titel } : {}) }];
  });
}

export async function ladeLayout(ansicht: string): Promise<LayoutEintrag[]> {
  const { data } = await api.get('/dashboard/layout', { params: { ansicht } });
  return leseLayoutEintraege(data?.data?.layout);
}

export async function speichereLayout(ansicht: string, layout: LayoutEintrag[]): Promise<void> {
  await api.put('/dashboard/layout', { ansicht, layout });
}

export async function speichereFarbstufe(farbstufe: string): Promise<void> {
  await api.put('/dashboard/einstellungen', { farbstufe });
}

// ---------------------------------------------------------------------------
// Phase 8: Daueraufträge und Hinweise
// ---------------------------------------------------------------------------
import {
  normalisiereDauerauftrag, normalisiereHinweis,
  type DauerauftragEintrag, type HinweisEintrag,
} from './dauerauftragLogik';

export type { DauerauftragEintrag, HinweisEintrag };

function dauerauftragPfad(id: string, aktion?: string): string {
  const basis = `/dashboard/dauerauftraege/${encodeURIComponent(String(id))}`;
  return aktion ? `${basis}/${aktion}` : basis;
}

function codeIst(err: unknown, status: number, code: string): boolean {
  const e = err as { response?: { status?: number; data?: { error?: { code?: string } } } };
  return e?.response?.status === status && e.response.data?.error?.code === code;
}

/** True bei 409 DAUERAUFTRAG_STATUS (Zustand hat sich zwischenzeitlich geaendert). */
export function istDauerauftragStatusFehler(err: unknown): boolean {
  return codeIst(err, 409, 'DAUERAUFTRAG_STATUS');
}

/** True bei 409 DAUERAUFTRAG_LIMIT. */
export function istDauerauftragLimitFehler(err: unknown): boolean {
  return codeIst(err, 409, 'DAUERAUFTRAG_LIMIT');
}

export async function ladeDauerauftraege(): Promise<DauerauftragEintrag[]> {
  const { data } = await api.get('/dashboard/dauerauftraege');
  const liste: unknown[] = Array.isArray(data?.data?.dauerauftraege) ? data.data.dauerauftraege : [];
  return liste.map(normalisiereDauerauftrag).filter((e): e is DauerauftragEintrag => e !== null);
}

export async function bestaetigeDauerauftrag(id: string): Promise<DauerauftragEintrag | null> {
  const { data } = await api.post(dauerauftragPfad(id, 'bestaetigen'));
  return normalisiereDauerauftrag(data?.data?.dauerauftrag);
}

export async function pausiereDauerauftrag(id: string): Promise<DauerauftragEintrag | null> {
  const { data } = await api.post(dauerauftragPfad(id, 'pausieren'));
  return normalisiereDauerauftrag(data?.data?.dauerauftrag);
}

export async function setzeDauerauftragFort(id: string): Promise<DauerauftragEintrag | null> {
  const { data } = await api.post(dauerauftragPfad(id, 'fortsetzen'));
  return normalisiereDauerauftrag(data?.data?.dauerauftrag);
}

/** Loescht einen Dauerauftrag (dient auch als "Verwerfen" eines Vorschlags). */
export async function loescheDauerauftrag(id: string): Promise<void> {
  await api.delete(dauerauftragPfad(id));
}

/** Startet einen Lauf im Hintergrund (Server antwortet 202). */
export async function fuehreDauerauftragAus(id: string): Promise<void> {
  await api.post(dauerauftragPfad(id, 'jetzt'));
}

export async function ladeHinweise(): Promise<HinweisEintrag[]> {
  const { data } = await api.get('/dashboard/hinweise');
  const liste: unknown[] = Array.isArray(data?.data?.hinweise) ? data.data.hinweise : [];
  return liste.map(normalisiereHinweis).filter((e): e is HinweisEintrag => e !== null);
}

export async function markiereHinweisGelesen(id: string): Promise<void> {
  await api.post(`/dashboard/hinweise/${encodeURIComponent(String(id))}/gelesen`);
}
